import { ChatLevelUp, ChatSuggestion } from '@/types/chat';
import { BackIcon, Button, PaperPlaneIcon, PencilIcon } from '@repo/atomic-components';
import { useState } from 'react';
import { useChatContext } from '../useChatContext';
import { classNames as cn } from 'primereact/utils';
import { useFrostedGlassClass } from '@/ui/theme/useFrostedGlassClass';
import { LevelUpAnimatedIcon } from './LevelUpAnimatedIcon';
import { KEEPS_CHAT_INPUT_OPEN_ATTR } from '../constants';

interface NextStepsProps {
  suggestions: ChatSuggestion[];
  levelUp?: ChatLevelUp[];
  /**
   * Called when a suggestion is picked (text or pencil) so the parent can expand the input. Not
   * called for the send arrow.
   */
  onSuggestionSelected?: () => void;
}

export const CHAT_INPUT_TEXTAREA_ID = 'chat-input-textarea';

export const NextSteps = ({ suggestions, levelUp, onSuggestionSelected }: NextStepsProps) => {
  const { handleSuggestionClick, handleSendMessage, isLoading } = useChatContext();
  const frostedBgClass = useFrostedGlassClass();
  const [isLevelUpOpen, setIsLevelUpOpen] = useState(false);

  const levelUpSuggestions = levelUp ?? [];
  const activeSuggestions = isLevelUpOpen ? levelUpSuggestions : suggestions;

  const suggestionSource = isLevelUpOpen ? 'level_up' : 'follow_up';

  const handleSuggestionPromptClick = (prompt: string) => {
    onSuggestionSelected?.();
    handleSuggestionClick(prompt, suggestionSource);
  };

  const handleEditSuggestion = (prompt: string) => {
    onSuggestionSelected?.();
    handleSuggestionClick(prompt, suggestionSource);
    // Focus the chat input so the user can edit before sending.
    requestAnimationFrame(() => {
      const el = document.getElementById(CHAT_INPUT_TEXTAREA_ID) as HTMLTextAreaElement | null;
      el?.focus();
    });
  };

  const rowClass = cn(
    'border-theme-border-grey/50 text-theme-black flex items-center gap-2 rounded-lg border pl-4 pr-3 py-2 text-left text-sm shadow-[0_1px_3px_rgba(0,0,0,0.08)] transition-all duration-300 ease-out will-change-transform hover:translate-x-2 hover:!bg-theme-white/50',
    frostedBgClass,
  );

  return (
    <div className="mt-4 text-sm">
      {isLevelUpOpen ? (
        <div className="text-theme-black mb-2 flex items-center gap-2 font-semibold">
          <button
            type="button"
            onClick={() => setIsLevelUpOpen(false)}
            aria-label="Back to next steps"
            className="text-theme-text-grey hover:text-theme-black flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors"
          >
            <BackIcon pathClassName="fill-current" />
          </button>
          <LevelUpAnimatedIcon />
          <span>Level up</span>
        </div>
      ) : (
        <div className="text-theme-black mb-2 font-semibold">Next steps</div>
      )}
      <div className="flex flex-col gap-2">
        {!isLevelUpOpen && levelUpSuggestions.length > 0 && (
          <button
            type="button"
            onClick={() => setIsLevelUpOpen(true)}
            className={cn(rowClass, 'group/level-up cursor-pointer')}
            aria-expanded={isLevelUpOpen}
          >
            <LevelUpAnimatedIcon />
            <span>Level up last response</span>
          </button>
        )}
        {activeSuggestions.map((suggestion, index) => (
          <div
            key={`${index}-${(suggestion as ChatSuggestion).title || suggestion.prompt}`}
            className={cn(rowClass, 'group/next-step')}
          >
            <span className="text-theme-text-grey inline-flex w-5 shrink-0 justify-center font-medium">
              {index + 1}.
            </span>
            <p
              onClick={() => handleSuggestionPromptClick(suggestion.prompt)}
              className="min-w-0 flex-1 cursor-pointer"
              {...{ [KEEPS_CHAT_INPUT_OPEN_ATTR]: '' }}
            >
              {suggestion.prompt}
            </p>
            <div className="pointer-events-none flex shrink-0 items-center gap-2 opacity-0 transition-opacity duration-150 group-focus-within/next-step:pointer-events-auto group-focus-within/next-step:opacity-100 group-hover/next-step:pointer-events-auto group-hover/next-step:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100">
              <button
                type="button"
                className="text-theme-text-grey hover:text-theme-black flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors"
                onClick={() => handleEditSuggestion(suggestion.prompt)}
                aria-label={`Edit suggestion ${index + 1} in chat input`}
                {...{ [KEEPS_CHAT_INPUT_OPEN_ATTR]: '' }}
              >
                <PencilIcon width={14} height={14} />
              </button>

              <Button
                type="button"
                disabled={isLoading}
                className="bg-theme-dark-green hover:bg-theme-dark-green focus-visible:ring-theme-dark-green flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-none p-0 focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() =>
                  void handleSendMessage(undefined, suggestion.prompt, undefined, {
                    suggestionArrow: suggestionSource,
                  })
                }
                aria-label={`Send suggestion ${index + 1}`}
              >
                <PaperPlaneIcon className="text-theme-white" width={16} height={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
